"use client";

import { useCallback, useEffect, useState } from "react";
import type { ComponentProps, ReactNode } from "react";
import { createLazyOverlay } from "./createLazyOverlay";
import type { ContextMenuPanel as ContextMenuPanelComponent } from "./ContextMenuPanel";
import { readContextMenuContext } from "./contextMenuEdit.utils";
import type { ContextMenuContext } from "./contextMenuEdit.utils";
export interface ContextMenuItem {
  label: ReactNode;
  icon?: ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
  isDestructive?: boolean;
  trailing?: ReactNode;
  submenu?: ContextMenuItem[];
}
const targetGroupsByEvent = new WeakMap<Event, ContextMenuItem[][]>();
export function ContextMenuTarget({
  groups,
  children
}: {
  groups: ContextMenuItem[][];
  children: ReactNode;
}) {
  return <span className="contents" onContextMenu={event => {
    if (targetGroupsByEvent.has(event.nativeEvent)) return;
    targetGroupsByEvent.set(event.nativeEvent, groups);
  }} data-sentry-component="ContextMenuTarget" data-sentry-source-file="ContextMenu.tsx">
            {children}
        </span>;
}
export function ContextMenuShortcut({
  keys
}: {
  keys: string;
}) {
  return <span className="ml-6 flex-shrink-0 font-mono text-footnote text-ink-3" data-sentry-component="ContextMenuShortcut" data-sentry-source-file="ContextMenu.tsx">
            {keys}
        </span>;
}
const LazyContextMenuPanel = createLazyOverlay<ComponentProps<typeof ContextMenuPanelComponent>>({
  name: "contextMenu",
  load: () => import("./ContextMenuPanel"),
  exportName: "ContextMenuPanel",
  renderFallback: props => props.isOpen && <DismissWhileLoading onDismiss={props.onClose} />
});
export function DismissWhileLoading({
  onDismiss
}: {
  onDismiss: () => void;
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onDismiss();
    };
    document.addEventListener("pointerdown", onDismiss, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onDismiss, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [onDismiss]);
  return null;
}
export function ContextMenu({
  groups,
  label,
  children,
  className,
  buildLeadingGroups
}: {
  groups: ContextMenuItem[][];
  label: string;
  children: ReactNode;
  className?: string;
  buildLeadingGroups?: (context: ContextMenuContext) => ContextMenuItem[][];
}) {
  const [menu, setMenu] = useState<{
    x: number;
    y: number;
    isOpen: boolean;
    openCount: number;
    groups: ContextMenuItem[][];
  } | null>(null);
  const close = useCallback(() => setMenu(current => current && {
    ...current,
    isOpen: false
  }), []);
  return <div className={className} onContextMenu={event => {
    if (event.defaultPrevented || event.shiftKey) return;
    const leadingGroups = targetGroupsByEvent.get(event.nativeEvent) ?? buildLeadingGroups?.(readContextMenuContext(event.target)) ?? [];
    const visibleGroups = [...leadingGroups, ...groups].filter(group => group.length > 0);
    if (visibleGroups.length === 0) return;
    event.preventDefault();
    setMenu(current => ({
      x: event.clientX,
      y: event.clientY,
      isOpen: true,
      openCount: (current?.openCount ?? 0) + 1,
      groups: visibleGroups
    }));
  }} data-sentry-component="ContextMenu" data-sentry-source-file="ContextMenu.tsx">
            {children}
            {menu && <LazyContextMenuPanel key={menu.openCount} groups={menu.groups} label={label} position={menu} isOpen={menu.isOpen} onClose={close} />}
        </div>;
}